// ===========================================
// konakla - Telefon numarası normalizasyonu
// ===========================================
//
// NEDEN VAR: `turkishPhoneRegex` (lib/validations/listing.ts:33) üç ayrı biçimi
// de kabul ediyor — `+905321234567`, `05321234567` ve `5321234567`. Bu doğru bir
// esneklik: ev sahibi numarasını alıştığı gibi yazabiliyor. Ama `wa.me` YALNIZCA
// ülke kodlu, baştaki sıfırı atılmış, saf rakam kabul eder. Link kurucular
// numaradan sadece rakam-dışı karakterleri temizleyip (`replace(/\D/g, "")`)
// doğrudan `wa.me/` sonuna ekliyordu; `05321234567` böyle geçince WhatsApp
// numarayı çözemez ve kullanıcı "numara geçersiz" ekranına düşer.
//
// Ölçüm (2026-08-20, canlı veri): WhatsApp'ı dolu 11 profilin yalnızca 3'ü
// geçerli biçimdeydi. Aktif ilanı olan 7 ev sahibinin 5'inin butonu kırıktı ve
// `contact_clicks` tablosunda 9 farklı IP'den gelen 10 gerçek tıklama boşa
// gitmişti.
//
// ÇÖZÜM OKUMA ANINDA: normalizasyon burada, gösterim yolunda yapılıyor. Böylece
// veritabanındaki mevcut kayıtlara dokunmadan hepsi çalışır hâle gelir; veri
// temizliği ayrıca yapılabilir ama düzeltmenin ön koşulu değildir.

/** Ülke kodu olmadan yazılmış numaralar için varsayılan ülke kodu (Türkiye). */
const VARSAYILAN_ULKE_KODU = "90";

/**
 * Bir telefon numarasını `wa.me` bağlantısının beklediği biçime çevirir:
 * ülke kodlu, saf rakam, baştaki sıfır atılmış. Örn. `"0532 123 45 67"` →
 * `"905321234567"`.
 *
 * Güvenle normalize edilemeyen girdide **`null`** döner. Çağıran taraf bu
 * durumda WhatsApp bağlantısını hiç göstermemelidir — kırık bir bağlantı
 * göstermek, hiç göstermemekten daha kötüdür: kullanıcı iletişim kurduğunu
 * sanır, ev sahibine mesaj ulaşmaz.
 */
export function toWhatsAppNumber(raw: string | null | undefined): string | null {
  if (!raw) return null;

  let digits = raw.replace(/\D/g, "");
  if (!digits) return null;

  // Uluslararası arama öneki: "0090 532..." → "90 532..."
  if (digits.startsWith("00")) digits = digits.slice(2);

  // --- Türkiye ---
  // Numaranın hangi biçimde yazıldığını tespit edip 10 haneli ABONE numarasına
  // indir. Türkiye'de abone numarası hiçbir zaman 0 ile başlamaz — bu kural
  // `turkishPhoneRegex`'teki `([1-9]\d{2})` grubuyla aynı (validations/listing.ts:33).
  let abone: string | null = null;
  if (digits.length === 10) {
    abone = digits; // "5321234567"
  } else if (digits.length === 11 && digits.startsWith("0")) {
    abone = digits.slice(1); // "05321234567"
  } else if (digits.length === 12 && digits.startsWith(VARSAYILAN_ULKE_KODU)) {
    abone = digits.slice(2); // "905321234567"
  } else if (digits.length === 13 && digits.startsWith(VARSAYILAN_ULKE_KODU + "0")) {
    abone = digits.slice(3); // "9005321234567" — ülke kodu + yerel sıfır birlikte
  }

  if (abone !== null) {
    // Baştan sıfırlı veya eksik haneli girdi buraya düşer ve REDDEDİLİR.
    // Örn. "0532123456" (10 hane, sıfırlı) bir hane eksiktir; tahmin etmek
    // mesajın yanlış kişiye gitmesine yol açabilir.
    return /^[1-9]\d{9}$/.test(abone) ? VARSAYILAN_ULKE_KODU + abone : null;
  }

  // "90" ile başlıyorsa Türkiye numarasıdır ve yukarıdaki kalıplardan birine
  // uymak zorundaydı. Uymuyorsa hane sayısı hatalıdır — yurt dışı sanıp
  // olduğu gibi geçirmek kırık bağlantı üretir.
  if (digits.startsWith(VARSAYILAN_ULKE_KODU)) return null;

  // --- Yurt dışı ---
  // Ülke kodu zaten yazılmış görünüyor (baştaki sıfır yok, E.164 uzunluk
  // aralığında). Olduğu gibi bırak — Türkiye kodu EKLEME.
  if (digits.length >= 11 && digits.length <= 15 && !digits.startsWith("0")) {
    return digits;
  }

  // Çok kısa, çok uzun veya yorumlanamayan biçim.
  return null;
}

/**
 * `toWhatsAppNumber` ile normalize edilmiş numaradan tam `wa.me` bağlantısı
 * üretir. Numara normalize edilemiyorsa `null` döner.
 *
 * @param text Ön-doldurulacak mesaj. Kodlanmamış hâlde verilir; burada
 *             `encodeURIComponent` uygulanır — çağıranın iki kez kodlaması
 *             (`%20` → `%2520`) sık yapılan bir hatadır.
 */
export function buildWhatsAppLink(raw: string | null | undefined, text?: string): string | null {
  const number = toWhatsAppNumber(raw);
  if (!number) return null;
  return text
    ? `https://wa.me/${number}?text=${encodeURIComponent(text)}`
    : `https://wa.me/${number}`;
}

// ===========================================
// Doğrulama
// ===========================================

/**
 * Türkiye telefon numarası doğrulama deseni.
 *
 * ⚠️ AYIRICI LİSTESİ BİLEREK GENİŞ. Eski desen ayırıcı olarak yalnızca boşluk
 * ve tire kabul ediyordu (`[\s-]?`) ve bu CANLI BİR KAYBA yol açtı: ev
 * sahibinin biri numarasını `0 (532) 434 53 32` biçiminde yazdı — telefon
 * tuş takımlarının ve rehberlerin ürettiği en yaygın biçim — ve doğrulama
 * bunu reddetti. Sihirbazda görünür bir hata da çıkmadığı için (bkz. Y-13)
 * kullanıcı sebebini hiç öğrenemedi; taslağı **altı hafta** yayınlanmadan
 * bekledi. Numara tamamen geçerliydi, yalnızca parantez içeriyordu.
 *
 * Ayırıcılar artık: boşluk, tire, parantez, nokta. Rakam kuralları AYNEN
 * korundu — abone numarası 1-9 ile başlar ve toplam 10 hanedir, yani
 * genişleme geçersiz numara kabul etmiyor (test edildi).
 */
export const TR_PHONE_PATTERN =
  /^(\+90|0)?[\s\-().]*([1-9]\d{2})[\s\-().]*(\d{3})[\s\-().]*(\d{2})[\s\-().]*(\d{2})$/;

/** Kullanıcının yazabildiği biçimlerde Türkiye telefon numarası mı? */
export function isValidTrPhone(value: string): boolean {
  return TR_PHONE_PATTERN.test(value);
}
